import type { WebType } from '@/types/api-generated'
import type { LandingPageSignedOffVersionDto, LandingPageTemplateListItemDto } from '@/types/landingPage'
import { dayOf } from '@/components/console/knowledgeBase/brandGuidelinesModel'
import { isOnlineEventType } from '@/components/webs/constants/webType'
import { isOnDemandMode } from '@/utils/onlineEventMode'
import { injectForcedEventState, prepareHtmlForPreview } from '@/utils/htmlPreviewStubs'

/** The order the Templates page lists its groups in. A Record, so a web type added later fails the build until it has a place. */
const WEB_TYPE_RANK: Record<WebType, number> = { 'Online Event': 0, Download: 1, Meeting: 2, Demo: 3 }

/** "v3 · signed off 2 Oct by Stuart D.", the version a page started from the template copies now. */
export function signedOffText(version: LandingPageSignedOffVersionDto, withSigner = true): string {
    return describeSignedOff(version, withSigner, ' · ')
}

/** "v3, signed off 2 Oct by Stuart D.", the same version as a clause in a sentence. */
export function signedOffClause(version: LandingPageSignedOffVersionDto): string {
    return describeSignedOff(version, true, ', ')
}

function describeSignedOff(version: LandingPageSignedOffVersionDto, withSigner: boolean, separator: string): string {
    const day = dayOf(version.SignedOffAt)
    const signer = withSigner && version.SignedOffByName ? ` by ${version.SignedOffByName}` : ''
    return day ? `v${version.VersionNumber}${separator}signed off ${day}${signer}` : `v${version.VersionNumber}${signer}`
}

/** "Upcoming (live scheduled) · Saved by Mary on 10 Oct". The event mode leads only on an online event's template. */
export function savedText(template: LandingPageTemplateListItemDto): string {
    const by = template.Template.CreatedByName ? ` by ${template.Template.CreatedByName}` : ''
    const day = dayOf(template.Template.CreatedUtc)
    const saved = `Saved${by}${day ? ` on ${day}` : ''}`
    return template.EventMode ? `${template.EventMode} · ${saved}` : saved
}

/** Whether a template's pages have a before and an after worth pinning in its preview: a live online event's do. */
export function templateHasEventStates(template: Pick<LandingPageTemplateListItemDto, 'WebType' | 'EventMode'>): boolean {
    return isOnlineEventType(template.WebType) && !isOnDemandMode(template.EventMode)
}

/**
 * A template's page made safe for a sandboxed frame, and a live event's pinned to before its event: that is the page a
 * new one starts on, while unpinned the page's own runtime would pick a state from the clock, and the event the template
 * was saved from has usually happened.
 */
export function templatePreviewHtml(template: Pick<LandingPageTemplateListItemDto, 'WebType' | 'EventMode'>, html: string): string {
    return prepareHtmlForPreview(templateHasEventStates(template) ? injectForcedEventState(html, 'pre') : html)
}

/**
 * The webs a page on this web can start from, in words. The server matches the web type and whether the event has a
 * live session, so an Upcoming web is offered Hybrid templates too and the words say "live", not the web's own mode.
 */
export function compatibleWebsLabel(webType: string | null | undefined, eventMode: string | null | undefined): string {
    if (isOnlineEventType(webType)) return isOnDemandMode(eventMode) ? 'On-demand Online Event' : 'live Online Event'
    return webType ?? 'the same kind of'
}

/** The templates under each web type, in the web types' order, each group keeping the list's newest-first order. */
export function groupByWebType(
    templates: LandingPageTemplateListItemDto[],
): Array<{ webType: WebType; templates: LandingPageTemplateListItemDto[] }> {
    return (Object.keys(WEB_TYPE_RANK) as WebType[])
        .sort((a, b) => WEB_TYPE_RANK[a] - WEB_TYPE_RANK[b])
        .map(webType => ({ webType, templates: templates.filter(template => template.WebType === webType) }))
        .filter(group => group.templates.length > 0)
}

/** Whether a template answers a search: its name, its campaign or its web, ignoring case. */
export function matchesTemplateSearch(template: LandingPageTemplateListItemDto, term: string): boolean {
    const needle = term.trim().toLowerCase()
    if (!needle) return true
    return [template.Template.Name, template.CampaignName, template.WebName]
        .some(value => value.toLowerCase().includes(needle))
}
